// =============================================================================
// StockerPostComposer.jsx
// =============================================================================
//
// PURPOSE:
//   Unified post composer that handles BOTH public feed posts AND group posts.
//   The two post types live in separate backend tables (posts / group_posts) —
//   this is correct architecture — but the UX and evidence logic is identical,
//   so we share it here behind a feature flag.
//
// FEATURE FLAG — groupMode:
//   When groupMode=true the composer:
//     - Shows a group selector UI (dropdown of the user's approved groups)
//     - Dispatches createGroupPost instead of createPost
//     - Passes group_post_id to createEvidenceThunk instead of post_id
//     - Shows a "Group Post" badge and adjusted footer text
//   When groupMode=false (default) it behaves exactly as before — no regressions.
//
// PROPS:
//   activeStock        {string}  — ticker symbol e.g. "AAPL"
//   setActiveStock     {fn}      — optional setter if parent controls ticker
//   watchlist          {Array}   — list of stock objects the user follows
//   stockData          {object}  — full stock object for the active ticker
//   onClose            {fn}      — called when user clicks X in header
//   onPostCreated      {fn}      — called after successful submit + 2s delay
//   contextAttachments {object}  — auto-attaches a filing/news context item
//                                  shape: { type, url, title, summary,
//                                           whyItMatters, form, publishedAt,
//                                           filingDate, source, description }
//   contextData        {object}  — alias for contextAttachments coming from
//                                  ExpandableStockCard's PostTab
//                                  shape: { type, evt, prefill }
//                                  We normalise this internally so both callers work.
//   groupMode          {bool}    — FEATURE FLAG: enable group posting UI
//                                  default: false
//   defaultGroupId     {number}  — pre-select a group (e.g. from GroupDetailPage)
//
// ORIGINAL LOGIC PRESERVED INTACT:
//   - FILING_DESCRIPTIONS dictionary
//   - contextAttachments auto-attach + duplicate guard
//   - Sentiment reset on stock switch (only when composer is empty)
//   - 15s submission timeout guard
//   - Evidence: quickAddEvidence, addEvidence, removeEvidence
//   - mapEvidenceType (frontend → backend enum)
//   - formatTimeAgo helper
//   - EvidencePickerPanel sub-component
//   - Success overlay with 2s delay before cleanup
//   - Stock price banner (desktop + mobile)
//   - All existing Tailwind classes and visual design
//
// =============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  TrendingDown,
  Send,
  X,
  FileText,
  Newspaper,
  Link as LinkIcon,
  AlertCircle,
  Plus,
  ChevronDown,
  ChevronUp,
  Calendar,
  ExternalLink,
  Check,
  Clock,
  Building2,
  Info,
  Users,        // NEW: used for group mode indicator
} from 'lucide-react';

// ── Public post actions (original) ────────────────────────────────────────────
import { createPost } from '../../../redux/posts/postActions';
import { createEvidenceThunk } from '../../../redux/posts/postEvidence/postEvidenceActions';

// ── Group post action (NEW) ────────────────────────────────────────────────────
// createGroupPost dispatches POST /group_posts/ and expects:
//   { group_id, title, content, evidence: [] }
// The backend (group_posts_endpoint.py) handles membership check, sentiment,
// moderation, and evidence attachment — same pipeline as public posts.
import { createGroupPost } from '../../../redux/userGroups/groupPost/userGroupesPosts';

// ── Stock data fetchers (original) ────────────────────────────────────────────
import { fetchEdgarFilingsData } from '../../../redux/stockData/edgar/edgarActions'; //create? check
import { fetchSentimentData } from '../../../redux/stockSentiment/sentimentActions';

// ── Groups fetcher (NEW) ───────────────────────────────────────────────────────
// fetchGroups fetches GET /groups/?limit=50 and stores in state.groups.groups
// Each group object has: { id, name, members: [{ user_id, status, role }] }
// We filter client-side to status === 'approved' which matches the backend
// _serialize_group_with_owner embed confirmed in our audit.
import { fetchGroups } from '../../../redux/userGroups/groups/userGroupies';

import { useNavigate } from 'react-router-dom';

// =============================================================================
// FILING_DESCRIPTIONS — unchanged from original
// Human-readable labels shown in the evidence picker for SEC filing types
// =============================================================================
// English fallback text -- used as the i18next defaultValue so the picker
// still shows something sensible if a form code has no translated entry yet.
const FILING_DESCRIPTIONS = {
  '4':       'Insider trading disclosure',
  '8-K':     'Major unscheduled event',
  '10-K':    'Annual financial report',
  '10-Q':    'Quarterly financial report',
  'S-1':     'IPO registration',
  'S-3':     'Secondary offering',
  'S-4':     'Business combination',
  'S-8':     'Employee benefit plan',
  '13F':     'Institutional holdings',
  '13D':     'Major ownership (5%+)',
  '13G':     'Passive ownership',
  'DEF 14A': 'Proxy statement',
  'SC 13D':  'Acquisition disclosure',
  'SC 13G':  'Passive ownership',
  '144':     'Restricted stock sale',
  '3':       'Initial insider ownership',
  '5':       'Annual insider summary',
  '6-K':     'Foreign issuer report',
  '20-F':    'Foreign annual report',
  'F-1':     'Foreign IPO registration',
};

// Translated lookup -- `form` (e.g. "8-K", "DEF 14A") is a real SEC form code,
// never renamed, only used to resolve the display text. `t` is passed in
// rather than called via a hook here since this helper runs outside any
// component's render (module scope / plain callbacks).
const getFilingDescription = (form, t) =>
  t(`dashboard.postComposer.filingDescriptions.${form}`, FILING_DESCRIPTIONS[form] || '');

// =============================================================================
// normaliseContextAttachments
// =============================================================================
// ExpandableStockCard's PostTab passes contextData={{ type, evt, prefill }}
// The original composer expected contextAttachments={{ type, url, title, ... }}
// This helper bridges both shapes so neither caller needs to change.
// Returns null if neither prop is usable.
// =============================================================================
const normaliseContextAttachments = (contextAttachments, contextData, t) => {
  // Priority: explicit contextAttachments prop (original callers)
  if (contextAttachments) return contextAttachments;

  // contextData comes from ExpandableStockCard PostTab
  if (!contextData) return null;

  const { type, evt, prefill } = contextData;

  if (type === 'event' && evt) {
    // Map event object → the shape the evidence auto-attach system expects
    return {
      type:        evt.event_type === 'filing' ? 'filing' : 'news',
      url:         evt.filing_url || evt.source_url || evt.url || null,
      title:       evt.headline || evt.title || evt.form || t('dashboard.postComposer.fallbackEventTitle'),
      summary:     evt.summary || evt.description || '',
      whyItMatters: evt.driver_summary || evt.interpretation_hint || '',
      form:         evt.form || null,
      publishedAt:  evt.published_at || evt.created_at || null,
      filingDate:   evt.filing_date || null,
      source:       evt.source || null,
      description:  evt.description || null,
      // Pre-fill the content textarea with the generated stub
      _prefill:     prefill || null,
    };
  }

  if (type === 'chart' || type === 'summary') {
    // Chart/summary context: no filing/news to attach, just pre-fill content
    return {
      type:     'link',
      url:      null,
      title:    null,
      _prefill: prefill || null,
      _noAutoAttach: true, // signal: don't add to evidence list, just prefill
    };
  }

  return null;
};

// =============================================================================
// DashboardPostComposer — main component
// =============================================================================
const DashboardPostComposer = ({
  activeStock,
  setActiveStock,
  watchlist = [],
  stockData,
  onClose,
  onPostCreated,

  // Original context attachment prop (from ExpandableStockCard via direct usage)
  contextAttachments = null,

  // Alternative context prop from PostTab in ExpandableStockCard
  // Shape: { type: 'event'|'chart'|'summary', evt, prefill }
  contextData = null,

  // ── FEATURE FLAG ────────────────────────────────────────────────────────────
  // groupMode: when true, shows group selector and dispatches to group_posts endpoint
  // Safe to leave false/undefined for all existing callers — zero behaviour change
  groupMode = false,

  // Optionally pre-select a specific group (e.g. when used inside GroupDetailPage)
  defaultGroupId = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ── Redux state — public posts (original) ──────────────────────────────────
  // state.posts only exists in the store when featureFlags.posts is enabled
  // (see redux/indexRedux.js), so this must not destructure it directly --
  // doing so throws whenever the flag is off, which crashes this composer the
  // moment it renders (reachable from the Dashboard by selecting an active
  // stock, independent of the posts flag).
  const { loading: createPostLoading } = useSelector(state => state.posts || {});

  // ── Redux state — filings + news (original) ────────────────────────────────
  const edgarState     = useSelector(state => state.edgar || {});
  const sentimentState = useSelector(state => state.sentiment || {});
  const filings        = edgarState.filings || [];
  const newsArticles   = sentimentState.sentimentData?.articles || [];

  // ── Redux state — groups (NEW, only used when groupMode=true) ──────────────
  // state.groups.groups comes from fetchGroups thunk.
  // Shape per group: { id, name, members: [{ user_id, status, role }] }
  const currentUserId = useSelector(state => state.auth?.user?.id);
  const allGroups     = useSelector(state => state.groups?.groups || []);

  // Filter to only groups where current user is an approved member.
  // This mirrors the backend _assert_member check in group_posts_endpoint.py
  // so the selector is never more permissive than the API.
  const myApprovedGroups = useMemo(() => {
    if (!groupMode || !currentUserId) return [];
    return allGroups.filter(g =>
      g.members?.some(
        m => m.user_id === currentUserId && m.status === 'approved'
      )
    );
  }, [allGroups, currentUserId, groupMode]);

  // ── Normalise incoming context (bridges contextData + contextAttachments) ──
  const normalisedContext = useMemo(
    () => normaliseContextAttachments(contextAttachments, contextData, t),
    [contextAttachments, contextData, t]
  );

  // ── Local state ────────────────────────────────────────────────────────────
  const [title,       setTitle]       = useState('');
  const [content,     setContent]     = useState('');
  const [sentiment,   setSentiment]   = useState(null);
  const [isExpanded,  setIsExpanded]  = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess,  setShowSuccess]  = useState(false);
  const [submitError,  setSubmitError]  = useState(null);

  // MAR Art 20: a person publishing an investment recommendation must
  // disclose whether they hold a position in the asset, shown alongside the
  // recommendation, not just captured and logged. See 08_MASTER_TODO.md #62.
  const [disclosesPosition, setDisclosesPosition] = useState(false);

  // Group selector state (only active when groupMode=true)
  const [selectedGroupId, setSelectedGroupId] = useState(defaultGroupId || null);
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);

  // Evidence state — unchanged from original
  const [showEvidenceModal,  setShowEvidenceModal]  = useState(false);
  const [showEvidencePicker, setShowEvidencePicker] = useState(false);
  const [evidenceItems,      setEvidenceItems]      = useState([]);
  const [currentEvidence,    setCurrentEvidence]    = useState({
    type:           'news',
    url:            '',
    title:          '',
    summary:        '',
    relevance_note: '',
  });

  // ── Effects ────────────────────────────────────────────────────────────────

  // Fetch filings + news when active stock changes — ORIGINAL, unchanged
  useEffect(() => {
    if (!activeStock) return;
    dispatch(fetchEdgarFilingsData(activeStock));
    dispatch(fetchSentimentData(activeStock));
  }, [activeStock, dispatch]);

  // Fetch user's groups when groupMode is enabled — NEW
  // Only fires once when groupMode is first true, keeps the list fresh.
  useEffect(() => {
    if (!groupMode) return;
    dispatch(fetchGroups({ limit: 50 }));
  }, [groupMode, dispatch]);

  // Apply defaultGroupId if provided and valid — NEW
  // Handles the case where GroupDetailPage pre-selects a group
  useEffect(() => {
    if (!groupMode || !defaultGroupId) return;
    setSelectedGroupId(defaultGroupId);
  }, [defaultGroupId, groupMode]);

  // Smart default: if the user belongs to exactly one group, just pick it for
  // them -- no dropdown to open, no decision to make. Only kicks in when
  // nothing is already selected (never overrides defaultGroupId or a manual
  // choice), and backs off automatically if they belong to zero or several.
  useEffect(() => {
    if (!groupMode || selectedGroupId || defaultGroupId) return;
    if (myApprovedGroups.length === 1) {
      setSelectedGroupId(myApprovedGroups[0].id);
    }
  }, [groupMode, myApprovedGroups, selectedGroupId, defaultGroupId]);

  // Auto-attach context when normalisedContext changes — ORIGINAL logic preserved,
  // now also handles the contextData shape from ExpandableStockCard's PostTab
  useEffect(() => {
    if (!normalisedContext) return;

    // _noAutoAttach means we only want to prefill text, not add to evidence list
    // (used for chart/summary contexts that have no URL to attach)
    if (normalisedContext._noAutoAttach) {
      if (normalisedContext._prefill && !content.trim()) {
        setContent(normalisedContext._prefill);
      }
      return;
    }

    // If there's a prefill stub and content is empty, apply it
    if (normalisedContext._prefill && !content.trim()) {
      setContent(normalisedContext._prefill);
    }

    // Skip if no title/url to attach as evidence
    if (!normalisedContext.title && !normalisedContext.url) return;

    // Duplicate guard — same as original
    const isDuplicate = evidenceItems.some(item =>
      (normalisedContext.url && item.url === normalisedContext.url) ||
      (item.title === normalisedContext.title && item.type === normalisedContext.type)
    );
    if (isDuplicate) return;

    // Build attachment object — same shape as original quickAddEvidence
    const newAttachment = {
      id:            Date.now(),
      type:          normalisedContext.type === 'filing' ? 'filing'
                   : normalisedContext.type === 'news'   ? 'news'
                   : 'link',
      url:           normalisedContext.url || '#',
      title:         normalisedContext.title || t('dashboard.postComposer.fallbackContextTitle'),
      summary:       normalisedContext.summary || '',
      relevance_note: normalisedContext.whyItMatters || '',
      form:           normalisedContext.form,
      publishedAt:    normalisedContext.publishedAt,
      filingDate:     normalisedContext.filingDate,
      source:         normalisedContext.source,
      description:    normalisedContext.description,
      autoAttached:   true, // shown as badge in evidence list
    };

    setEvidenceItems(prev => [...prev, newAttachment]);

    // Pre-fill title if empty — original behaviour
    if (!title.trim() && normalisedContext.title) {
      setTitle(normalisedContext.title);
    }
  }, [normalisedContext]); // Only re-run when context changes

  // Reset sentiment when switching stocks (only if composer is empty) — ORIGINAL
  useEffect(() => {
    if (!activeStock) return;
    const isComposerEmpty =
      !title.trim() && !content.trim() && evidenceItems.length === 0;
    if (isComposerEmpty) setSentiment(null);
  }, [activeStock]);

  // Submission timeout guard — ORIGINAL (prevents indefinite spinner)
  useEffect(() => {
    if (!isSubmitting) return;
    const t = setTimeout(() => setIsSubmitting(false), 15000);
    return () => clearTimeout(t);
  }, [isSubmitting]);

  // ── Validation ─────────────────────────────────────────────────────────────
  // Group mode requires a group to be selected in addition to title/content.
  // Length bounds added to match the backend's PostCreate/GroupPostCreate
  // Field(min_length=10, max_length=200) / Field(min_length=1, max_length=10000)
  // constraints -- this composer previously only checked non-empty, so a
  // 1-character title or a 500,000-character content string both passed
  // client-side and hit an unconstrained backend.
  const isValid =
    title.trim().length >= 10 &&
    title.trim().length <= 200 &&
    content.trim().length > 0 &&
    content.trim().length <= 10000 &&
    (!groupMode || selectedGroupId !== null);

  // ── Submit handler ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (isSubmitting || !isValid) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (groupMode) {
        // ── GROUP POST PATH ──────────────────────────────────────────────────
        // Dispatches POST /group_posts/ via createGroupPost thunk.
        //
        // Payload matches GroupPostCreate schema (group_post.py):
        //   { group_id, title, content, evidence: [...] }
        //
        // Evidence is sent inline in the payload because the backend
        // group_posts_endpoint.py handles evidence attachment server-side
        // in the same transaction as post creation — no separate thunk needed.
        // This is different from public posts where evidence is a separate call.
        //
        // The backend already handles:
        //   - Membership check (_assert_member)
        //   - Sentiment analysis
        //   - Auto-moderation
        //   - Member notifications
        //   - Evidence attachment (PostEvidenceCreate loop)

        const validEvidence = evidenceItems
          .filter(e => e.url?.trim() && e.title?.trim())
          .map(e => ({
            evidence_type:  mapEvidenceType(e.type),
            source_url:     e.url,
            title:          e.title,
            excerpt:        e.summary   || null,
            relevance_note: e.relevance_note || null,
          }));

        const groupPostData = {
          groupId: selectedGroupId,
          title:    title.trim(),
          // Prepend $TICKER just like public posts so backend can parse ticker
          // mentions for future auto-association features
          content: activeStock
            ? `$${activeStock} ${content.trim()}`
            : content.trim(),
          evidence: validEvidence,
          disclosesPosition,
        };

        await dispatch(
          createGroupPost(groupPostData)
        ).unwrap();

      } else {
        // ── PUBLIC POST PATH — ORIGINAL, unchanged ───────────────────────────
        // Dispatches POST /posts/ via createPost thunk.
        // Then separately dispatches evidence via createEvidenceThunk.
        // This two-step approach is kept as-is to avoid regressions.

        const postData = {
          title:        title.trim(),
          content: activeStock
            ? `$${activeStock} ${content.trim()}`
            : content.trim(),
          stock_ids:     stockData?.id ? [stockData.id] : [],
          discloses_position: disclosesPosition,
        };
        // sentiment_hint deliberately not sent: PostCreate has no such
        // field (confirmed via repo-wide grep) -- Pydantic silently
        // dropped it, so the Bullish/Bearish quick-select above never
        // actually influenced the stored sentiment either way. Real
        // sentiment_type/sentiment_score always come from server-side
        // VADER+AI fusion, regardless of this button.

        const postResult = await dispatch(
          createPost({ newPostData: postData })
        ).unwrap();

        // Attach evidence as separate calls (original pattern)
        if (evidenceItems.length > 0 && postResult?.id) {
          const validEvidence = evidenceItems.filter(
            e => e.url?.trim() && e.title?.trim()
          );
          await Promise.all(
            validEvidence.map(evidence =>
              dispatch(createEvidenceThunk({
                post_id:        postResult.id,
                evidence_type:  mapEvidenceType(evidence.type),
                source_url:     evidence.url,
                title:          evidence.title,
                excerpt:        evidence.summary,
                relevance_note: evidence.relevance_note,
              })).unwrap()
            )
          );
        }
      }

      // ── Success — shared by both paths ────────────────────────────────────
      setShowSuccess(true);
      setIsSubmitting(false);

      // 2s delay then clean up and notify parent — original timing preserved
      setTimeout(() => {
        setTitle('');
        setContent('');
        setSentiment(null);
        setEvidenceItems([]);
        setDisclosesPosition(false);
        setShowSuccess(false);
        // Reset group selection only if it wasn't locked via defaultGroupId
        if (!defaultGroupId) setSelectedGroupId(null);
        onPostCreated?.();
      }, 2000);

    } catch (err) {
      // Log but don't crash — same pattern as original
      console.warn('[PostComposer] submit failed', err);
      setSubmitError(typeof err === 'string' ? err : (err?.message || t('dashboard.postComposer.fallbackSubmitError')));
      setIsSubmitting(false);
    }
  };

  // ── Evidence helpers — ALL ORIGINAL, unchanged ────────────────────────────

  // Maps frontend evidence type labels → backend EvidenceType enum values
  // (defined in post_evidence_model.py EvidenceType enum)
  const mapEvidenceType = (frontendType) => {
    const mapping = {
      'news':    'news_article',
      'filing':  'sec_filing',
      'link':    'other',
    };
    return mapping[frontendType] || 'other';
  };

  // Quick-add from evidence picker panel (filing or news item)
  const quickAddEvidence = (item, type) => {
    if (evidenceItems.length >= 3) {
      alert(t('dashboard.postComposer.maxEvidenceItems'));
      return;
    }
    const isDuplicate = evidenceItems.some(e =>
      e.url   === (item.url || item.filing_url || item.source_url) ||
      e.title === (item.title || item.form || item.headline)
    );
    if (isDuplicate) {
      alert(t('dashboard.postComposer.evidenceAlreadyAttached'));
      return;
    }
    const evidence = {
      id:            Date.now(),
      type,
      url:           item.url || item.filing_url || item.source_url || '#',
      title:         item.title || item.headline || item.form || t('dashboard.postComposer.fallbackUntitled'),
      summary:       item.summary || item.filing_type || item.description || '',
      relevance_note: '',
      form:          item.form,
      filingDate:    item.filing_date || item.date,
      publishedAt:   item.published_at || item.publishedAt || item.date,
      source:        item.source || item.publisher,
      description:   item.description || (item.form ? getFilingDescription(item.form, t) : ''),
      autoAttached:  false,
    };
    setEvidenceItems([...evidenceItems, evidence]);
    setShowEvidencePicker(false);
  };

  // Manual evidence add from the manual-entry modal
  const addEvidence = () => {
    if (!currentEvidence.url.trim() || !currentEvidence.title.trim()) return;
    // Was accepting any non-empty string as a URL -- GroupPostComposer.jsx's
    // equivalent manual-entry flow already validates format; this one didn't.
    try {
      new URL(currentEvidence.url.trim());
    } catch {
      alert(t('dashboard.postComposer.invalidEvidenceUrl'));
      return;
    }
    if (evidenceItems.length >= 3) {
      alert(t('dashboard.postComposer.maxEvidenceItems'));
      return;
    }
    const isDuplicate = evidenceItems.some(e =>
      e.url === currentEvidence.url || e.title === currentEvidence.title
    );
    if (isDuplicate) {
      alert(t('dashboard.postComposer.evidenceAlreadyAttached'));
      return;
    }
    setEvidenceItems([...evidenceItems, {
      ...currentEvidence,
      id:           Date.now(),
      autoAttached: false,
    }]);
    setCurrentEvidence({ type: 'news', url: '', title: '', summary: '', relevance_note: '' });
    setShowEvidenceModal(false);
  };

  const removeEvidence = (id) => {
    setEvidenceItems(evidenceItems.filter(item => item.id !== id));
  };

  // Evidence display helpers — ORIGINAL
  const getEvidenceIcon = (type) => {
    switch (type) {
      case 'news':   return <Newspaper className="w-4 h-4" />;
      case 'filing': return <FileText  className="w-4 h-4" />;
      default:       return <LinkIcon  className="w-4 h-4" />;
    }
  };

  const getEvidenceColor = (type) => {
    switch (type) {
      case 'news':   return 'from-blue-600/20 to-blue-500/10 border-blue-500/30';
      case 'filing': return 'from-purple-600/20 to-purple-500/10 border-purple-500/30';
      default:       return 'from-gray-600/20 to-gray-500/10 border-gray-300 dark:border-gray-500/30';
    }
  };

  // Date formatting helper — ORIGINAL
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now  = new Date();
    const days = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (days === 0)  return t('dashboard.postComposer.today');
    if (days === 1)  return t('dashboard.postComposer.yesterday');
    if (days < 7)   return t('dashboard.postComposer.daysAgo', { count: days });
    if (days < 30)  return t('dashboard.postComposer.weeksAgo', { count: Math.floor(days / 7) });
    return date.toLocaleDateString();
  };

  // Word count for content textarea — ORIGINAL
  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  // ── Derived UI values ──────────────────────────────────────────────────────
  const selectedGroup = myApprovedGroups.find(g => g.id === selectedGroupId) || null;

  // ============================================================================
  // SUCCESS OVERLAY — ORIGINAL design, success message adapted for group mode
  // ============================================================================
  if (showSuccess) {
    return (
      <div className="bg-gradient-to-r from-emerald-600/10 to-blue-600/10 border border-emerald-500/20 rounded-2xl p-6 animate-pulse">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
            <Send className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="text-center">
            <h3 className="text-lg font-bold text-white">{t('dashboard.postComposer.success.title')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-600 dark:text-gray-400">
              {groupMode && selectedGroup
                ? t('dashboard.postComposer.success.sharedToGroup', { ticker: activeStock, group: selectedGroup.name })
                : t('dashboard.postComposer.success.shared', { ticker: activeStock })}
              {evidenceItems.length > 0 &&
                t('dashboard.postComposer.success.evidenceSuffix', { count: evidenceItems.length })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <>
      <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm border border-blue-500/20 rounded-2xl overflow-hidden shadow-2xl">

        {/* ── HEADER BAR — ORIGINAL + group mode badge ── */}
        <div
          className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600/10 to-purple-600/10 border-b border-gray-200 dark:border-gray-700/50 cursor-pointer hover:bg-blue-600/5 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 bg-gradient-to-br rounded-lg flex items-center justify-center ${
              groupMode
                ? 'from-emerald-600 to-teal-600'   // distinct colour when in group mode
                : 'from-blue-600 to-purple-600'
            }`}>
              {groupMode
                ? <Users className="w-5 h-5 text-white" />
                : <Send  className="w-5 h-5 text-white" />
              }
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white">{t('dashboard.postComposer.header.title')}</h3>
                {/* Group mode badge — NEW */}
                {groupMode && (
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-full">
                    {t('dashboard.postComposer.header.groupPostBadge')}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-600 dark:text-gray-400">
                {activeStock
                  ? groupMode && selectedGroup
                    ? t('dashboard.postComposer.header.postingAboutGroup', { ticker: activeStock, group: selectedGroup.name })
                    : t('dashboard.postComposer.header.postingAbout', { ticker: activeStock })
                  : t('dashboard.postComposer.header.selectStockToPost')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Stock price chip — ORIGINAL */}
            {stockData && (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/stocks/${activeStock}`); }}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gray-200 dark:bg-gray-700/50 rounded-lg hover:bg-gray-200 dark:bg-gray-700/70 transition"
              >
                <span className="text-xs font-medium text-gray-300 dark:text-gray-600 dark:text-gray-300">${activeStock}</span>
                <div className="h-3 w-px bg-gray-300 dark:bg-gray-600" />
                <span className="text-xs font-bold text-white">${stockData.price?.toFixed(2)}</span>
                <span className={`text-xs font-semibold ${stockData.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {stockData.change >= 0 ? '+' : ''}{stockData.change?.toFixed(2)}%
                </span>
              </button>
            )}

            {/* Close button — ORIGINAL */}
            <button
              onClick={(e) => { e.stopPropagation(); onClose?.(); }}
              className="p-2 hover:bg-gray-200 dark:bg-gray-700/50 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-500 dark:text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* ── EXPANDABLE BODY ── */}
        {isExpanded && (
          <div className="p-4 space-y-4">

            {/* Context attachment banner — ORIGINAL logic, now handles contextData too */}
            {normalisedContext && !normalisedContext._noAutoAttach && normalisedContext.title && (
              <div className="bg-blue-100 dark:bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-blue-700 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-blue-300 mb-1">
                      {t('dashboard.postComposer.context.autoAttachedLabel')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      {normalisedContext.type === 'filing' && normalisedContext.form && (
                        <span className="font-semibold">{normalisedContext.form}: </span>
                      )}
                      {normalisedContext.title}
                      {normalisedContext.publishedAt && (
                        <span className="ml-2 text-gray-500 dark:text-slate-500">
                          • {new Date(normalisedContext.publishedAt).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Mobile stock banner — ORIGINAL */}
            {stockData && (
              <div className="sm:hidden bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/20 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-white">${activeStock}</span>
                    <span className="text-sm text-gray-300 dark:text-gray-600 dark:text-gray-300">${stockData.price?.toFixed(2)}</span>
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-semibold ${stockData.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {stockData.change >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {stockData.change >= 0 ? '+' : ''}{stockData.change?.toFixed(2)}%
                  </div>
                </div>
              </div>
            )}

            {/* ── GROUP SELECTOR — NEW, only rendered when groupMode=true ── */}
            {groupMode && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  {t('dashboard.postComposer.groupSelector.label')}
                  <span className="text-red-400">*</span>
                </label>

                {myApprovedGroups.length === 0 ? (
                  // User has no approved groups — show helpful empty state
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-100 dark:bg-gray-700/30 border border-gray-300 dark:border-gray-300 dark:border-gray-600/40 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span className="text-xs text-gray-500 dark:text-gray-600 dark:text-gray-400">
                      {t('dashboard.postComposer.groupSelector.noGroups')}
                    </span>
                  </div>
                ) : (
                  // Group dropdown
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setGroupDropdownOpen(o => !o); }}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm transition-all ${
                        selectedGroupId
                          ? 'bg-emerald-600/10 border-emerald-500/30 text-white'
                          : 'bg-gray-100 dark:bg-gray-700/30 border-gray-300 dark:border-gray-300 dark:border-gray-600/40 text-gray-500 dark:text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:border-gray-500/60'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        <span>
                          {selectedGroup ? selectedGroup.name : t('dashboard.postComposer.groupSelector.selectPlaceholder')}
                        </span>
                      </div>
                      {groupDropdownOpen
                        ? <ChevronUp  className="w-4 h-4 text-gray-500 dark:text-gray-600 dark:text-gray-400 flex-shrink-0" />
                        : <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-600 dark:text-gray-400 flex-shrink-0" />
                      }
                    </button>

                    {groupDropdownOpen && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 rounded-xl shadow-2xl overflow-hidden">
                        <div className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800/50">
                          {myApprovedGroups.map(group => (
                            <button
                              key={group.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedGroupId(group.id);
                                setGroupDropdownOpen(false);
                              }}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-200 dark:bg-gray-700/50 transition-colors ${
                                selectedGroupId === group.id
                                  ? 'bg-emerald-600/10 text-emerald-300'
                                  : 'text-gray-900 dark:text-gray-200'
                              }`}
                            >
                              {/* Checkmark for selected group */}
                              <div className={`w-4 h-4 flex-shrink-0 rounded-full border flex items-center justify-center ${
                                selectedGroupId === group.id
                                  ? 'bg-emerald-500 border-emerald-500'
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}>
                                {selectedGroupId === group.id && (
                                  <Check className="w-2.5 h-2.5 text-white" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{group.name}</p>
                                {group.description && (
                                  <p className="text-xs text-gray-500 dark:text-gray-500 truncate">{group.description}</p>
                                )}
                              </div>
                              {/* Member count badge */}
                              <span className="text-xs text-gray-500 dark:text-gray-500 flex-shrink-0">
                                {t('dashboard.postComposer.groupSelector.members', { count: group.members?.length ?? 0 })}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Validation hint — only show when user tried to submit without group */}
                {!selectedGroupId && (
                  <p className="text-[10px] text-gray-500 dark:text-gray-500 px-1">
                    {t('dashboard.postComposer.groupSelector.validationHint')}
                  </p>
                )}
              </div>
            )}

            {/* ── SENTIMENT QUICK SELECT ── */}
            {/* This selection is local UI state only -- neither the public
                nor group-post payload transmits it (see handleSubmit above).
                Real sentiment_type/sentiment_score always come from
                server-side VADER+AI fusion. If self-reported sentiment
                should ever actually influence the stored value, that's a
                separate product decision, not something to infer here. */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-600 dark:text-gray-400 font-medium">{t('dashboard.postComposer.sentiment.label')}</span>
              <button
                onClick={() => setSentiment(sentiment === 'bull' ? null : 'bull')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  sentiment === 'bull'
                    ? 'bg-emerald-600/20 text-emerald-300 border-2 border-emerald-500/30 shadow-lg shadow-emerald-500/10'
                    : 'bg-gray-100 dark:bg-gray-700/30 text-gray-500 dark:text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-300 dark:border-gray-600/30'
                }`}
              >
                <TrendingUp size={14} />
                <span>{t('dashboard.postComposer.sentiment.bullish')}</span>
              </button>
              <button
                onClick={() => setSentiment(sentiment === 'bear' ? null : 'bear')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  sentiment === 'bear'
                    ? 'bg-red-600/20 text-red-300 border-2 border-red-500/30 shadow-lg shadow-red-500/10'
                    : 'bg-gray-100 dark:bg-gray-700/30 text-gray-500 dark:text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-300 dark:border-gray-600/30'
                }`}
              >
                <TrendingDown size={14} />
                <span>{t('dashboard.postComposer.sentiment.bearish')}</span>
              </button>
              {sentiment && (
                <button
                  onClick={() => setSentiment(null)}
                  className="text-xs text-gray-500 dark:text-gray-500 hover:text-gray-300 dark:text-gray-600 dark:text-gray-300"
                >
                  {t('dashboard.postComposer.sentiment.clear')}
                </button>
              )}
            </div>

            {/* ── POSITION DISCLOSURE — MAR Art 20. Was gated behind the
                 sentiment quick-select above, so a purely-prose directional
                 call (no Bullish/Bearish click) never triggered the
                 compliance prompt at all. Now shown whenever there's real
                 content to disclose against, independent of that button. ── */}
            {content.trim().length > 0 && (
              <label className="flex items-start gap-2.5 px-3.5 py-3 bg-amber-500/5 border border-amber-500/20 rounded-xl cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={disclosesPosition}
                  onChange={(e) => setDisclosesPosition(e.target.checked)}
                  disabled={isSubmitting}
                  className="mt-0.5 w-4 h-4 accent-amber-500 flex-shrink-0"
                />
                <span className="text-xs text-gray-300 dark:text-gray-300 leading-relaxed">
                  <span className="font-medium text-white">{t('dashboard.postComposer.disclosure.statement')}</span>{' '}
                  {activeStock
                    ? t('dashboard.postComposer.disclosure.inTicker', { ticker: activeStock })
                    : t('dashboard.postComposer.disclosure.inAssets')}{' '}
                  {t('dashboard.postComposer.disclosure.suffix')}
                </span>
              </label>
            )}

            {/* ── TITLE INPUT — ORIGINAL ── */}
            <div>
              <input
                type="text"
                placeholder={t('dashboard.postComposer.titlePlaceholder')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-3 bg-gray-200 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-300 dark:border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                disabled={isSubmitting}
              />
            </div>

            {/* ── CONTENT TEXTAREA — ORIGINAL ── */}
            <div>
              <textarea
                placeholder={t('dashboard.postComposer.contentPlaceholder')}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-gray-200 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-300 dark:border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent resize-none transition-all"
                disabled={isSubmitting}
              />
              <div className="flex items-center justify-between mt-1 px-1">
                <span className="text-xs text-gray-500 dark:text-gray-500">
                  {t('dashboard.postComposer.word', { count: wordCount })}
                </span>
                {evidenceItems.length > 0 && (
                  <span className="text-xs text-blue-400">
                    {t('dashboard.postComposer.evidenceAttached', { count: evidenceItems.length })}
                  </span>
                )}
              </div>
            </div>

            {/* ── EVIDENCE ITEMS DISPLAY — ORIGINAL, all display logic intact ── */}
            {evidenceItems.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-600 dark:text-gray-400">
                  <FileText size={14} />
                  <span>{t('dashboard.postComposer.evidenceSectionTitle', { count: evidenceItems.length })}</span>
                </div>
                {evidenceItems.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-start gap-3 p-3 bg-gradient-to-r ${getEvidenceColor(item.type)} border rounded-lg group hover:border-opacity-60 transition-all`}
                  >
                    <div className="flex-shrink-0 mt-1">{getEvidenceIcon(item.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="text-sm font-semibold text-white">{item.title}</p>
                            {item.autoAttached && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded border border-blue-500/30 flex-shrink-0">
                                {t('dashboard.postComposer.autoAttachedBadge')}
                              </span>
                            )}
                          </div>

                          {/* Filing-specific details — ORIGINAL */}
                          {item.type === 'filing' && (
                            <div className="space-y-1 mb-2">
                              {item.form && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded border border-purple-500/30 font-mono">
                                    {item.form}
                                  </span>
                                  {item.description && (
                                    <span className="text-xs text-gray-500 dark:text-gray-600 dark:text-gray-400">{item.description}</span>
                                  )}
                                </div>
                              )}
                              {item.filingDate && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-600 dark:text-gray-400">
                                  <Calendar className="w-3 h-3" />
                                  <span>{t('dashboard.postComposer.filedLabel', { date: new Date(item.filingDate).toLocaleDateString() })}</span>
                                  <span className="text-gray-500 dark:text-gray-500">({formatTimeAgo(item.filingDate)})</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* News-specific details — ORIGINAL */}
                          {item.type === 'news' && (
                            <div className="space-y-1 mb-2">
                              {item.source && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-600 dark:text-gray-400">
                                  <Building2 className="w-3 h-3" />
                                  <span className="font-medium text-blue-300">{item.source}</span>
                                </div>
                              )}
                              {item.publishedAt && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-600 dark:text-gray-400">
                                  <Clock className="w-3 h-3" />
                                  <span>{t('dashboard.postComposer.publishedLabel', { date: new Date(item.publishedAt).toLocaleDateString() })}</span>
                                  <span className="text-gray-500 dark:text-gray-500">({formatTimeAgo(item.publishedAt)})</span>
                                </div>
                              )}
                              {item.summary && (
                                <p className="text-xs text-gray-300 dark:text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed">
                                  {item.summary}
                                </p>
                              )}
                            </div>
                          )}

                          {/* URL display — ORIGINAL */}
                          {item.url && item.url !== '#' && (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors group/link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="truncate max-w-[200px]">{item.url}</span>
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover/link:opacity-100 transition-opacity flex-shrink-0" />
                            </a>
                          )}

                          {/* Relevance note — ORIGINAL */}
                          {item.relevance_note && (
                            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700/40">
                              <div className="flex items-start gap-1.5">
                                <Info className="w-3 h-3 text-gray-500 dark:text-gray-500 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-gray-300 dark:text-gray-600 dark:text-gray-300 italic leading-relaxed">
                                  "{item.relevance_note}"
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Remove button — ORIGINAL */}
                        <button
                          onClick={() => removeEvidence(item.id)}
                          className="flex-shrink-0 p-1.5 hover:bg-red-100 dark:hover:bg-red-500/10 rounded transition-colors group-hover:opacity-100 opacity-60"
                          title={t('dashboard.postComposer.removeEvidenceTitle')}
                        >
                          <X size={14} className="text-gray-500 dark:text-gray-600 dark:text-gray-400 hover:text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── ADD EVIDENCE BUTTONS ── */}
            {evidenceItems.length < 3 && (
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowEvidencePicker(!showEvidencePicker); setShowEvidenceModal(false); }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-200 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-300 dark:border-gray-600/50 rounded-lg text-sm text-gray-300 dark:text-gray-600 dark:text-gray-300 transition-colors"
                >
                  <Plus size={16} />
                  <span>{t('dashboard.postComposer.addEvidenceButton')}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {t('dashboard.postComposer.addEvidenceCounts', { filings: filings.length, news: newsArticles.length })}
                  </span>
                </button>
                {/* addEvidence() below has always had complete validation logic
                    (URL format, duplicates, 3-item cap) but no button ever opened
                    the modal it submits -- mirrors GroupPostComposer.jsx's
                    "Custom Link" trigger for the same manual-entry pattern. */}
                <button
                  onClick={() => { setShowEvidenceModal(!showEvidenceModal); setShowEvidencePicker(false); }}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-200 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-lg text-sm text-gray-300 dark:text-gray-600 dark:text-gray-300 transition-colors"
                >
                  <LinkIcon size={16} />
                  <span>{t('dashboard.postComposer.customLinkButton')}</span>
                </button>
              </div>
            )}

            {/* ── EVIDENCE PICKER PANEL — ORIGINAL ── */}
            {showEvidencePicker && (
              <EvidencePickerPanel
                filings={filings}
                newsArticles={newsArticles}
                onQuickAdd={quickAddEvidence}
                onClose={() => setShowEvidencePicker(false)}
                formatTimeAgo={formatTimeAgo}
              />
            )}

            {/* ── MANUAL EVIDENCE ENTRY MODAL ── */}
            {showEvidenceModal && (
              <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-300 dark:border-gray-700 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{t('dashboard.postComposer.customLinkButton')}</h4>
                  <button
                    onClick={() => setShowEvidenceModal(false)}
                    className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700/60 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex gap-2">
                  {[
                    { type: 'news', label: t('dashboard.postComposer.evidenceTypeNews') },
                    { type: 'filing', label: t('dashboard.postComposer.evidenceTypeSec') },
                    { type: 'other', label: t('dashboard.postComposer.evidenceTypeOther') },
                  ].map(({ type, label }) => (
                    <button
                      key={type}
                      onClick={() => setCurrentEvidence({ ...currentEvidence, type })}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                        currentEvidence.type === type
                          ? 'bg-blue-600/20 text-blue-300 border-blue-500/40'
                          : 'bg-gray-100 dark:bg-gray-700/30 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600/40'
                      }`}
                    >
                      {getEvidenceIcon(type)}
                      {label}
                    </button>
                  ))}
                </div>

                <input
                  type="text"
                  value={currentEvidence.title}
                  onChange={(e) => setCurrentEvidence({ ...currentEvidence, title: e.target.value })}
                  placeholder={t('dashboard.postComposer.evidenceTitlePlaceholder')}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                />
                <input
                  type="url"
                  value={currentEvidence.url}
                  onChange={(e) => setCurrentEvidence({ ...currentEvidence, url: e.target.value })}
                  placeholder={t('dashboard.postComposer.evidenceUrlPlaceholder')}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 font-mono"
                />
                <textarea
                  value={currentEvidence.relevance_note}
                  onChange={(e) => setCurrentEvidence({ ...currentEvidence, relevance_note: e.target.value })}
                  placeholder={t('dashboard.postComposer.evidenceRelevancePlaceholder')}
                  rows={2}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600/50 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none"
                />

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setShowEvidenceModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700/40 hover:bg-gray-300 dark:hover:bg-gray-700/60 text-gray-600 dark:text-gray-300 text-sm rounded-lg transition-colors"
                  >
                    {t('dashboard.postComposer.evidenceCancel')}
                  </button>
                  <button
                    onClick={addEvidence}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600/80 hover:bg-blue-600 text-white text-sm rounded-lg font-medium transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    {t('dashboard.postComposer.evidenceAttachButton')}
                  </button>
                </div>
              </div>
            )}

            {/* ── SUBMIT ERROR — e.g. the verify-your-email action gate ── */}
            {submitError && (
              <p className="text-sm text-red-400 text-center">{submitError}</p>
            )}

            {/* ── SUBMIT BUTTON ── */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSubmit}
                disabled={!isValid || isSubmitting}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                  isValid && !isSubmitting
                    ? groupMode
                      // Group mode: teal gradient to visually distinguish
                      ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/20'
                      // Public mode: original blue-purple gradient
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/20'
                    : 'bg-gray-100 dark:bg-gray-700/30 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>{t('dashboard.postComposer.submitting')}</span>
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    <span>
                      {groupMode
                        ? selectedGroup
                          ? t('dashboard.postComposer.postToGroup', { group: selectedGroup.name })
                          : t('dashboard.postComposer.postToGroupGeneric')
                        : t('dashboard.postComposer.publishAnalysis')}
                    </span>
                  </>
                )}
              </button>
            </div>

            {/* ── FOOTER NOTE — adapted for group vs public ── */}
            <p className="text-xs text-gray-500 dark:text-gray-500 text-center leading-relaxed">
              {groupMode
                ? t('dashboard.postComposer.footerGroup')
                : t('dashboard.postComposer.footerPublic')
              }
            </p>

          </div>
        )}
      </div>
    </>
  );
};

// =============================================================================
// EvidencePickerPanel — ORIGINAL, completely unchanged
// Rendered inside DashboardPostComposer when user clicks "Add Evidence"
// Shows tabs for SEC Filings and News Articles fetched from Redux
// =============================================================================
const EvidencePickerPanel = ({ filings, newsArticles, onQuickAdd, onClose, formatTimeAgo }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('filings');

  return (
    <div className="bg-white dark:bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('filings')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              activeTab === 'filings'
                ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30'
                : 'text-gray-500 dark:text-gray-600 dark:text-gray-400 hover:text-gray-300 dark:text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700/30'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>{t('dashboard.postComposer.pickerFilingsTab')}</span>
            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">
              {filings.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('news')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              activeTab === 'news'
                ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30'
                : 'text-gray-500 dark:text-gray-600 dark:text-gray-400 hover:text-gray-300 dark:text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700/30'
            }`}
          >
            <Newspaper className="w-4 h-4" />
            <span>{t('dashboard.postComposer.pickerNewsTab')}</span>
            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs">
              {newsArticles.length}
            </span>
          </button>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-500 dark:text-gray-600 dark:text-gray-400 hover:text-gray-300 dark:text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:bg-gray-700/50 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="max-h-96 overflow-y-auto space-y-2 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800/50">

        {/* FILINGS TAB */}
        {activeTab === 'filings' && filings.length > 0 ? (
          filings.slice(0, 10).map((filing, idx) => (
            <button
              key={idx}
              onClick={() => onQuickAdd(filing, 'filing')}
              className="w-full flex items-start gap-3 p-3 bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-200 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-300 dark:border-gray-600/30 hover:border-purple-500/40 rounded-lg text-left transition-all group"
            >
              <div className="flex-shrink-0 mt-1">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                  <FileText className="w-4 h-4 text-purple-400" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-purple-300 font-mono">
                      {filing.form}
                    </span>
                    {FILING_DESCRIPTIONS[filing.form] && (
                      <span className="text-xs text-gray-500 dark:text-gray-600 dark:text-gray-400">
                        {getFilingDescription(filing.form, t)}
                      </span>
                    )}
                  </div>
                </div>
                {filing.filing_type && (
                  <p className="text-xs text-gray-300 dark:text-gray-600 dark:text-gray-300 mb-1 line-clamp-2">{filing.filing_type}</p>
                )}
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-500 flex-wrap">
                  {filing.filing_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(filing.filing_date).toLocaleDateString()}
                    </span>
                  )}
                  {filing.filing_date && (
                    <span className="text-gray-300 dark:text-gray-600">{formatTimeAgo(filing.filing_date)}</span>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <Plus className="w-5 h-5 text-purple-400" />
              </div>
            </button>
          ))

        ) : activeTab === 'news' && newsArticles.length > 0 ? (
          /* NEWS TAB */
          newsArticles.slice(0, 10).map((article, idx) => (
            <button
              key={idx}
              onClick={() => onQuickAdd(article, 'news')}
              className="w-full flex items-start gap-3 p-3 bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-200 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-300 dark:border-gray-600/30 hover:border-blue-500/40 rounded-lg text-left transition-all group"
            >
              <div className="flex-shrink-0 mt-1">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <Newspaper className="w-4 h-4 text-blue-400" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white mb-1 line-clamp-2 leading-relaxed group-hover:text-blue-300 transition-colors">
                  {article.title || article.headline}
                </p>
                {article.source && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <Building2 className="w-3 h-3 text-gray-500 dark:text-gray-500" />
                    <span className="text-xs font-medium text-blue-300">{article.source}</span>
                  </div>
                )}
                {article.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-600 dark:text-gray-400 mb-2 line-clamp-2 leading-relaxed">
                    {article.description}
                  </p>
                )}
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-500 flex-wrap">
                  {(article.published_at || article.publishedAt || article.date) && (
                    <>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(article.published_at || article.publishedAt || article.date).toLocaleDateString()}
                      </span>
                      <span className="text-gray-300 dark:text-gray-600">
                        {formatTimeAgo(article.published_at || article.publishedAt || article.date)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <Plus className="w-5 h-5 text-blue-400" />
              </div>
            </button>
          ))

        ) : (
          /* EMPTY STATE */
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700/30 rounded-full flex items-center justify-center mx-auto mb-3">
              {activeTab === 'filings'
                ? <FileText  className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                : <Newspaper className="w-8 h-8 text-gray-300 dark:text-gray-600" />
              }
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-500 font-medium mb-1">
              {activeTab === 'filings'
                ? t('dashboard.postComposer.pickerEmptyFilings')
                : t('dashboard.postComposer.pickerEmptyNews')}
            </p>
            <p className="text-xs text-gray-300 dark:text-gray-600">
              {activeTab === 'filings'
                ? t('dashboard.postComposer.pickerEmptyFilingsHint')
                : t('dashboard.postComposer.pickerEmptyNewsHint')}
            </p>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700/50">
        <p className="text-xs text-gray-500 dark:text-gray-500 text-center">
          {t('dashboard.postComposer.pickerFooterHint')}
        </p>
      </div>
    </div>
  );
};

export default DashboardPostComposer;